(ns frontend.view.settings2.funds.view
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.permissions.team-permissions :as team-permissions]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.view.entity :as f-entity]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.fund-edit.view :as fund-edit]
   [frontend.view.settings2.funds.db :as db]
   [frontend.view.settings2.funds.events :as events]
   [oops.core :as oops]
   [reagent.core :as r]
   [ui.components.breadcrumbs.view :as breadcrumbs]
   [ui.components.button.view :as button]
   [ui.components.grid.view :as grid]))

(defn clear-selected!
  [state]
  (let [clear-selection (fn []
                          (let [grid-ref (get-in @state [:grid :ref])
                                grid-api (aget grid-ref "api")
                                deselect-all-fn (aget grid-api "deselectAll")]
                            (.call deselect-all-fn grid-api nil "apiDeselectAll")))]
    (swap! state dissoc :selected-fund-id)
    (clear-selection)))

(defn feeder-cell-renderer
  [feeder-ids]
  (let [feeder-entities (->> feeder-ids
                             (mapv session/get-entity))]
    (if (seq feeder-entities)
      (str (->> feeder-entities
                (map entity/display-string)
                (string/join ", ")))
      "-")))

(defn team-renderer
  [teams]
  (let [teams (->> teams
                   (map (fn [t]
                          (if (:db/id t)
                            t
                            (session/get-entity t)))))
        team-count (count teams)]
    (cond
      (zero? team-count)
      "-"
      (> team-count 5)
      (str (->> teams
                (map entity/display-string)
                (sort)
                (take 3)
                (string/join ", "))
           " and "
           (- team-count 3)
           " other teams")
      :else
      (->> teams
           (map entity/display-string)
           (sort)
           (string/join ", ")))))

(defn get-cell-value
  [props]
  (let [get-value-fn (aget props "getValue")
        value (.call get-value-fn props)]
    value))

(defn breadcrumbs
  []
  [breadcrumbs/container
   [breadcrumbs/item
    {:on-click (fn [_e]
                 (routing/set-view! [:account-settings :tab-key "config"]))}
    (loc/tr :settings/account-settings)]
   [breadcrumbs/item
    {:active? true}
    (loc/tr :settings/funds)]])

(defn resolve-row-data
  [fund]
  (let [active-user-ids @session/active-user-accounts-set
        disabled? (:common/disabled? fund)
        fund-teams (->> fund
                        :fund/teams
                        (map session/get-entity)
                        (remove :common/disabled?))
        fund-reporting-admins (->> fund
                                   :fund/reporting-admins
                                   (map session/get-entity)
                                   (remove :common/disabled?))
        active-user-accounts (->> (concat fund-teams fund-reporting-admins)
                                  (mapcat (fn [team]
                                            (team-permissions/resolve-active-team-members
                                             team
                                             active-user-ids)))
                                  distinct
                                  (count))]
    (merge fund
           {:fund/status          (if disabled?
                                    (loc/tr :disabled)
                                    (loc/tr :active))
            :fund/active-accounts active-user-accounts})))

(defn resolve-columns
  []
  (concat
   [{:headerName        (loc/tr :settings.funds/table-heading-status)
     :sortable          true
     :resizable         true
     :width             130
     :field             :fund/status}
    {:headerName (loc/tr :settings.funds/table-heading-name)
     :sortable   true
     :resizable  true
     :field      :fund/name}
    {:headerName (loc/tr :settings.funds/table-heading-members)
     :sortable   true
     :resizable  true
     :width      150
     :field      :fund/active-accounts}
    {:headerName   (loc/tr :settings.funds/table-heading-teams)
     :sortable     true
     :resizable    true
     :field        :fund/teams
     :cellRenderer (fn [props]
                     (team-renderer (get-cell-value props)))}]
   (when (and (session/has-feature-access? :feature/lp-reporting)
              (session/is-admin?))
     [{:headerName   (loc/tr :settings.funds/table-heading-reporting-admins)
       :sortable     true
       :resizable    true
       :field        :fund/reporting-admins
       :cellRenderer (fn [props]
                       (team-renderer (get-cell-value props)))}])
   (when (and (session/is-admin?)
              (f-permissions/field-read-access? :fund/feeder-fund-to
                                                :entity.type/fund
                                                nil))
     [{:headerName   (loc/tr :settings.funds/table-heading-feeder-of)
       :sortable     true
       :resizable    true
       :field        :fund/feeder-fund-to
       :cellRenderer (fn [props]
                       (feeder-cell-renderer (js->clj (get-cell-value props))))}])
   (when (f-permissions/field-read-access? :fund/lux-fund?
                                           :entity.type/fund
                                           nil)
     [{:headerName   (loc/tr :settings.funds/table-heading-lux-fund)
       :sortable     true
       :resizable    true
       :field        :fund/lux-fund?
       :width        140
       :cellRenderer (fn [props]
                       (loc/tr (if (get-cell-value props) :true :false)))}])))

(defn tools
  [state all-teams]
  (let [fund-id (:selected-fund-id @state)
        fund (session/get-entity fund-id)
        disabled-fund? (:common/disabled? fund)]
    [:div
     {:class-name "flex gap-3"}
     [button/helper-button
      {:disabled? (not fund-id)
       :tooltip   (when fund-id
                    (loc/tr :disabled-fund))
       :on-click  (fn [_]
                    (modals/open-modal! fund-edit/modal-key
                                        {:fund-id        (:db/id fund)
                                         :selected-fund  fund
                                         :existing-teams all-teams
                                         :on-close!      (fn []
                                                           (clear-selected! state))}))}
      [:i.fal.fa-pen]
      (loc/tr :edit-fund)]
     (when-not disabled-fund?
       [button/helper-button
        {:disabled? (not fund-id)
         :on-click  (fn [_]
                      (events/start-delete-fund! fund))}
        [:i.fal.fa-trash-alt]
        (loc/tr :delete-fund)])]))

(defn component
  [_state _on-selection-changed]
  (r/create-class
   {:reagent-render
    (fn [state on-selection-changed]
      (let [all-teams @session/teams
            all-funds @session/funds
            resolved-funds (->> all-funds
                                (map resolve-row-data)
                                (seq))]
        [:<>
         [zap-toolbar/view db/state breadcrumbs nil nil]
         [:div
          {:class "bg-base-light h-[calc(100vh_-_52px-_50px)] overflow-auto"}
          [:div
           {:class "px-4 py-6 mx-auto max-w-5xl"}
           (if (empty? all-funds)
             [:div
              {:class "flex flex-col gap-6 justify-center items-center"}
              [:img
               {:src        "/img/no-data-created.svg"
                :class-name "max-w-32 mx-auto"}]
              [button/sm-primary-solid
               {:dom-id   "create-first-fund-button"
                :on-click (fn [_]
                            (f-entity/open-creator! :entity.type/fund
                                                    {:on-ok! (fn [{:keys [_new-entity-id close-cb!]}]
                                                               (close-cb!))}))}
               [:i {:class "fal fa-plus"}] (loc/tr :create-your-first-fund)]]
             [:div
              {:class "flex flex-col gap-3"}
              [:span
               {:class "text-xl text-semibold"}
               (loc/tr :settings.funds/funds-heading)]
              [:div {:class "flex justify-between items-center"}
               [:p (loc/tr :settings.funds/top-text)]

               [button/sm-primary-solid
                {:dom-id   "create-fund-button"
                 :on-click (fn [_]
                             (f-entity/open-creator! :entity.type/fund
                                                     {:on-ok! (fn [{:keys [_new-entity-id close-cb!]}]
                                                                (close-cb!))}))}
                [:span [:i {:class "fal fa-plus"}]] (loc/tr :settings.funds/toolbar-create-fund)]]
              [:div
               [:div
                {:class "py-2 flex gap-3 text-base-light-text"}
                [tools state all-teams]]
               [grid/ag-grid-enterprise
                {:theme                grid/theme-quartz
                 :onGridReady          (fn [event]
                                         (swap! state assoc-in [:grid :ref] event))
                 :columnDefs          (resolve-columns)
                 :rowData             resolved-funds
                 :defaultColDef       {:sortable true
                                       :editable false
                                       :menuTabs ["generalMenuTab"]}
                 :row-selection        {:mode "singleRow"}
                 :min-height           "300px"
                 :domLayout            :autoHeight
                 :width                "1000px"
                 :popupParent          (oops/oget js/document "body")
                 :on-selection-changed on-selection-changed}]]])]]]))}))

(defmethod routing/render-view :settings/funds
  [_]
  (let [state (r/atom {})
        on-selection-changed (fn [params]
                               (let [grid-api (aget params "api")
                                     selected-nodes-js (oops/ocall grid-api "getSelectedNodes")
                                     selected-nodes (js->clj selected-nodes-js :keywordize-keys true)
                                     selected-node (first selected-nodes)]
                                 (if selected-node
                                   (let [selected-node-data (aget selected-node "data")
                                         selected-node-id (:id (js->clj selected-node-data {:keywordize-keys true}))]
                                     (swap! state assoc :selected-fund-id selected-node-id))
                                   (swap! state dissoc :selected-fund-id))))]
    [default-page-layout/view
     [component state on-selection-changed]]))
